import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { Search, ArrowUpRight, UserPlus } from "lucide-react";
import {
  searchCategory,
  searchLabels,
  searchTypes,
  type SearchFilter,
  type SearchItem,
} from "../api/global-search.api";
import { useAuthStore } from "../store/auth.store";
import { useCanEditCandidate } from "../views/candidates/detail/useOverviewData";
import {
  newStudentHref,
  newStudentLabels,
  studentFromSearch,
} from "../views/today/quickActionsModel";
import "./GlobalSearch.css";

const recentByScope = new Map<
  string,
  { search: string; type: SearchFilter }[]
>();
useAuthStore.subscribe((state, previous) => {
  if (
    state.user?.id !== previous.user?.id ||
    state.user?.role !== previous.user?.role ||
    !state.token
  )
    recentByScope.clear();
});
const filters: SearchFilter[] = [
  "ALL",
  "CANDIDATE",
  "TASK",
  "NOTE",
  "MEETING",
  "INVOICE",
];
export function GlobalSearch() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const schoolId = useAuthStore((s) => s.activeSchoolId);
  const scope = `${user?.id}:${user?.role}:${schoolId}`;
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const openRequested = useRef(false);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [type, setType] = useState<SearchFilter>("ALL");
  const [active, setActive] = useState<string | null>(null);
  const canCreate = useCanEditCandidate();
  const allowed = [
    "owner",
    "manager",
    "agent",
    "read_only",
    "super_admin",
  ].includes(user?.role ?? "");
  const types = type === "ALL" ? searchTypes : [type];
  // Independent queries display fast categories immediately, without waiting for slower ones.
  const results = useQueries({
    queries: types.map((t) => ({
      queryKey: ["directory-search", scope, t, search, page],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        searchCategory(search, t, page, signal),
      enabled: allowed && open && search.length >= 2 && text.trim() === search,
      staleTime: 0,
      gcTime: 0,
      retry: false,
    })),
  });
  const current = text.trim() === search && search.length >= 2;
  const groups = current
    ? results.flatMap((r) => (!r.isError && r.data ? [r.data] : []))
    : [];
  const items = groups.flatMap((g) =>
    type === "ALL" ? g.items.slice(0, 3) : g.items,
  );
  const key = (item: SearchItem) => `${item.type}-${item.id}`;
  const selected = items.find((i) => key(i) === active) ?? items[0];
  const errors = current
    ? results.flatMap((r, i) => (r.isError ? [searchLabels[types[i]]] : []))
    : [];
  const pending = text.trim() !== search || results.some((r) => r.isFetching);
  // Aucun résultat, élèves compris : proposer d'inscrire l'élève sous le nom, le téléphone
  // ou l'e-mail tapé (pas pour un NEPH ou un numéro incomplet ; rôles qui peuvent créer un élève).
  const newStudent =
    canCreate &&
    current &&
    !pending &&
    !items.length &&
    page === 1 &&
    types.includes("CANDIDATE") &&
    !errors.includes(searchLabels.CANDIDATE)
      ? studentFromSearch(search)
      : null;
  const newStudentText = newStudent && newStudentLabels(newStudent);
  const close = () => {
    openRequested.current = false;
    setOpen(false);
  };
  const launch = () => {
    openRequested.current = true;
    returnFocus.current = document.activeElement as HTMLElement;
    setText("");
    setSearch("");
    setType("ALL");
    setPage(1);
    setActive(null);
    setOpen(true);
  };
  const filter = (value: SearchFilter) => {
    setType(value);
    setPage(1);
    setActive(null);
    input.current?.focus();
  };
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(text.trim());
      setPage(1);
      setActive(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [text]);
  useEffect(() => {
    close();
    setText("");
    setSearch("");
    setType("ALL");
    setPage(1);
  }, [scope]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        allowed &&
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "k"
      ) {
        event.preventDefault();
        if (openRequested.current) close();
        else launch();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allowed]);
  useEffect(() => {
    if (open) {
      dialog.current?.showModal();
      input.current?.focus();
    } else dialog.current?.close();
  }, [open]);
  useEffect(() => {
    if (open && active)
      document
        .getElementById(`search-result-${active}`)
        ?.scrollIntoView({ block: "nearest" });
  }, [active, open]);
  const choose = (item: SearchItem) => {
    const old = recentByScope.get(scope) ?? [];
    recentByScope.set(
      scope,
      [
        { search, type },
        ...old.filter((x) => x.search !== search || x.type !== type),
      ].slice(0, 5),
    );
    close();
    navigate(item.href);
  };
  const createStudent = () => {
    close();
    navigate(newStudentHref(search));
  };
  if (!allowed) return null;
  return (
    <>
      <button
        type="button"
        className="global-search-trigger"
        onClick={launch}
        aria-label="Rechercher Ctrl K"
        aria-keyshortcuts="Control+k Meta+k"
      >
        <Search size={15} aria-hidden /> <span>Rechercher</span> <kbd>Ctrl K</kbd>
      </button>
      {createPortal(
        <dialog
          ref={dialog}
          className="global-search-dialog"
          aria-label="Recherche Permio"
          onCancel={(e) => {
            e.preventDefault();
            close();
          }}
          onClose={() => {
            // Un ancien close peut arriver avant que l'effet showModal ait
            // appliqué la nouvelle demande d'ouverture au dialogue natif.
            if (openRequested.current || dialog.current?.open) return;
            setOpen(false);
            if (returnFocus.current?.isConnected) returnFocus.current.focus();
          }}
          onClick={(e) => {
            if (e.target === dialog.current) {
              const r = dialog.current.getBoundingClientRect();
              if (
                e.clientX < r.left ||
                e.clientX > r.right ||
                e.clientY < r.top ||
                e.clientY > r.bottom
              )
                close();
            }
          }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && /^[1-6]$/.test(e.key)) {
              e.preventDefault();
              filter(filters[Number(e.key) - 1]);
            }
          }}
        >
          <div className="global-search-bar">
            <Search size={27} aria-hidden="true" />
            <input
              ref={input}
              id="global-search-input"
              aria-label="Rechercher dans Permio"
              placeholder="Nom, téléphone, NEPH, tâche…"
              value={text}
              maxLength={100}
              autoComplete="off"
              onChange={(e) => {
                setText(e.target.value);
                setActive(null);
              }}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={items.length > 0 || !!newStudent}
              aria-controls="global-search-results"
              aria-activedescendant={
                selected
                  ? `search-result-${key(selected)}`
                  : newStudent
                    ? "search-result-new-student"
                    : undefined
              }
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  if (items.length) {
                    const i = selected ? items.indexOf(selected) : 0;
                    setActive(
                      key(
                        items[
                          (i +
                            (e.key === "ArrowDown" ? 1 : -1) +
                            items.length) %
                            items.length
                        ],
                      ),
                    );
                  }
                }
                if (e.key === "Enter" && selected && current) {
                  e.preventDefault();
                  choose(selected);
                } else if (e.key === "Enter" && newStudent) {
                  e.preventDefault();
                  createStudent();
                }
              }}
            />
            <button
              type="button"
              tabIndex={-1}
              className="global-search-close"
              aria-label="Fermer la recherche"
              onClick={close}
            >
              <kbd>Échap</kbd>
            </button>
          </div>
          <div className="global-search-filters" aria-label="Catégories">
            {filters.map((t, index) => (
              <button
                type="button"
                key={t}
                aria-pressed={type === t}
                onClick={() => filter(t)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    e.preventDefault();
                    filter(
                      filters[
                        (index +
                          (e.key === "ArrowRight" ? 1 : -1) +
                          filters.length) %
                          filters.length
                      ],
                    );
                  }
                }}
              >
                {t === "INVOICE" ? "Factures" : searchLabels[t]}
              </button>
            ))}
            <select
              aria-label="Autres catégories"
              value={filters.includes(type) ? "" : type}
              onChange={(e) => {
                if (e.target.value) filter(e.target.value as SearchFilter);
              }}
            >
              <option value="">Autres…</option>
              {searchTypes
                .filter((t) => !filters.includes(t))
                .map((t) => (
                  <option key={t} value={t}>
                    {searchLabels[t]}
                  </option>
                ))}
            </select>
          </div>
          <div className="global-search-body">
            {text.trim().length < 2 && (
              <div className="global-search-empty">
                <p>Un élève, une tâche, une réunion…</p>
                <span>
                  Deux caractères suffisent · école active uniquement.
                </span>
                {!!recentByScope.get(scope)?.length && (
                  <div className="global-search-recents">
                    <span>Recherches récentes</span>
                    {recentByScope.get(scope)?.map((r) => (
                      <button
                        type="button"
                        key={r.type + r.search}
                        onClick={() => {
                          filter(r.type);
                          setText(r.search);
                        }}
                      >
                        {r.search} <small>{searchLabels[r.type]}</small>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        recentByScope.delete(scope);
                        setText(" ");
                      }}
                    >
                      Effacer
                    </button>
                  </div>
                )}
              </div>
            )}
            <p role="status" className="global-search-status">
              {pending && text.trim().length >= 2
                ? "Recherche…"
                : errors.length
                  ? `Recherche partielle : ${errors.join(", ")} indisponible(s).`
                  : current && !items.length
                    ? "Aucun résultat accessible."
                    : ""}
            </p>
            {errors.length > 0 && (
              <button
                type="button"
                className="global-search-retry"
                onClick={() =>
                  results
                    .filter((r) => r.isError)
                    .forEach((r) => void r.refetch())
                }
              >
                Réessayer
              </button>
            )}
            <div
              id="global-search-results"
              role="listbox"
              aria-label="Résultats"
              className="global-search-results"
            >
              {groups
                .filter((g) => g.items.length)
                .map((g) => (
                  <div
                    key={g.type}
                    role="group"
                    aria-label={searchLabels[g.type]}
                  >
                    <div
                      className="global-search-group-title"
                      aria-hidden="true"
                    >
                      {searchLabels[g.type]}
                    </div>
                    {(type === "ALL" ? g.items.slice(0, 3) : g.items).map(
                      (item) => (
                        <div
                          key={item.id}
                          id={`search-result-${key(item)}`}
                          role="option"
                          aria-selected={selected === item}
                          className="global-search-option"
                          onMouseMove={() => setActive(key(item))}
                          onClick={() => choose(item)}
                        >
                          <span
                            className="global-search-result-icon"
                            aria-hidden="true"
                          >
                            {item.label.slice(0, 1).toUpperCase()}
                          </span>
                          <div>
                            <strong>{item.label}</strong>
                            <span>{item.detail}</span>
                          </div>
                          <ArrowUpRight size={17} aria-hidden="true" />
                        </div>
                      ),
                    )}
                  </div>
                ))}
              {newStudentText && (
                <div
                  id="search-result-new-student"
                  role="option"
                  aria-selected="true"
                  className="global-search-option"
                  onClick={createStudent}
                >
                  <span
                    className="global-search-result-icon"
                    aria-hidden="true"
                  >
                    <UserPlus size={16} />
                  </span>
                  <div>
                    <strong>{newStudentText.title}</strong>
                    <span>{newStudentText.detail}</span>
                  </div>
                  <ArrowUpRight size={17} aria-hidden="true" />
                </div>
              )}
            </div>
            {type === "ALL" &&
              groups.some((g) => g.hasMore || g.items.length > 3) && (
                <div className="global-search-more">
                  {groups
                    .filter((g) => g.hasMore || g.items.length > 3)
                    .map((g) => (
                      <button
                        type="button"
                        key={g.type}
                        onClick={() => filter(g.type)}
                      >
                        Voir plus · {searchLabels[g.type]}
                      </button>
                    ))}
                </div>
              )}
            {type !== "ALL" && current && (
              <div className="global-search-pagination">
                <button
                  type="button"
                  disabled={page === 1 || pending}
                  onClick={() => {
                    setPage((p) => p - 1);
                    setActive(null);
                    input.current?.focus();
                  }}
                >
                  Précédent
                </button>
                <span>Page {page}</span>
                <button
                  type="button"
                  disabled={!groups[0]?.hasMore || pending}
                  onClick={() => {
                    setPage((p) => p + 1);
                    setActive(null);
                    input.current?.focus();
                  }}
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
          <footer className="global-search-footer">
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> Naviguer
            </span>
            <span>
              <kbd>Entrée</kbd> Ouvrir
            </span>
            <span>
              <kbd>Tab</kbd> Filtres
            </span>
          </footer>
        </dialog>,
        document.body,
      )}
    </>
  );
}
